import {ErrorBoundary} from "@sentry/react";
import {BarEventStatus} from "@uptime-com/uptimebar";
import {differenceInHours} from "date-fns";
import {Link} from "react-router";

import {Card} from "@/components/card/Card.tsx";
import {Dot} from "@/components/dot/Dot.tsx";
import {MonitoringServiceType, PerformanceDataType} from "@/lib/types/checks.ts";
import {CheckResponseTimeOverview} from "@/pages/checks/analysis/overview/CheckResponseTimeOverview.tsx";
import {CheckOverviewProps} from "@/pages/checks/analysis/overview/checktypes/CheckOverview.tsx";
import {DaysToExpireOverview} from "@/pages/checks/analysis/overview/DaysToExpireOverview.tsx";
import {Top5AlertsTable} from "@/pages/checks/analysis/overview/Top5AlertsTable.tsx";
import {getIntervalAlerts} from "@/pages/checks/analysis/overview/utils.ts";
import {UptimeBarCheckAnalysis} from "@/pages/checks/components/details/UptimeBar/UptimeBarCheckAnalysis.tsx";

export const DefaultCheckOverview = ({
  checkId,
  checkType,
  start,
  end,
  createdAt,
  intervals,
  flattenAlerts,
  flattenAlertsTotal,
  locations,
  responseTimes,
  dataType,
  days,
  onRangeChange,
  interval,
  uptimeBarEvents,
}: CheckOverviewProps) => {
  const intervalAlerts = getIntervalAlerts(intervals);
  const hoursRange = differenceInHours(end, start);
  const uptimeChartAvailable = hoursRange > 2;

  return (
    <div className="d-flex flex-column gap-4">
      {dataType === PerformanceDataType.DAYS_TO_EXPIRY && <DaysToExpireOverview days={days} dataType={dataType} />}
      {uptimeChartAvailable && (
        <Card variant="white" className="p-4">
          <div className="row mb-3">
            <div className={"d-flex justify-content-between mb-5 flex-wrap"}>
              <div className={"pulseTextH2Bold"}>Uptime</div>
              <div className={"d-flex gap-3 flex-end flex-wrap"}>
                <span className="d-flex align-items-center gap-1 pulseTextBodySmall">
                  <Dot variant={"green"} size={"legend"} />
                  Uptime
                </span>
                <span className="d-flex align-items-center gap-1 pulseTextBodySmall">
                  <Dot variant={"red"} size={"legend"} />
                  Downtime
                </span>
                <span className="d-flex text-nowrap align-items-center gap-1 pulseTextBodySmall">
                  <Dot variant={"gray"} size={"legend"} />
                  No Data
                </span>
              </div>
            </div>
          </div>

          <ErrorBoundary>
            <UptimeBarCheckAnalysis
              start={start}
              end={end}
              interval={interval}
              createdAt={createdAt}
              responseTimes={responseTimes}
              intervalAlerts={intervalAlerts}
              uptimeBarEvents={uptimeBarEvents}
              noResponseTimesDefaultStatus={
                [MonitoringServiceType.WEBHOOK, MonitoringServiceType.WHOIS, MonitoringServiceType.MALWARE].includes(
                  checkType
                )
                  ? BarEventStatus.UP
                  : BarEventStatus.NO_DATA
              }
            />
          </ErrorBoundary>
        </Card>
      )}
      {[PerformanceDataType.RESPONSE_TIME, PerformanceDataType.JITTER_DELTA, PerformanceDataType.CUSTOM].includes(
        dataType
      ) && (
        <ErrorBoundary>
          <Card variant="white">
            <CheckResponseTimeOverview
              start={start}
              dataType={dataType}
              end={end}
              locations={locations}
              intervalAlerts={intervalAlerts}
              responseTimes={responseTimes}
              onRangeChange={onRangeChange}
            />
          </Card>
        </ErrorBoundary>
      )}
      <Card variant="white" className="pb-2">
        <div className="pulseTextH2Bold pb-4">Alert Log</div>

        <Top5AlertsTable checkId={checkId} flattenAlerts={flattenAlerts} flattenAlertsTotal={flattenAlertsTotal} />

        {flattenAlertsTotal - flattenAlerts.length > 0 && (
          <div className="col p-2">
            <Link
              to={`/alerts?service_id=${checkId}&range_from=${encodeURIComponent(start.toISOString())}&range_to=${encodeURIComponent(end.toISOString())}`}
            >
              + {flattenAlertsTotal - flattenAlerts.length} more
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
};
